// What the editor says about a page belonging to a translation group.
//
// A group has ONE structure and many contents: which elements a page is made
// of, in what order, with what name, tags and frame, is the group's, and the
// prose inside them is this language's. api/code/src/model/page enforces that
// on every save; @common/schema/page/translation is the line itself; this file
// is what the screen says about it, so the sentence an author reads and the
// rule the server applies come from one place.
//
// It is the analogue of ./locales and ../translations for the same reason both
// of those give: a second copy of a rule is how the two stop agreeing.
import React from "react";
import { Badge } from "reactstrap";
import { localeOf } from "@common/render/page";
import { translationProgress } from "@common/schema/page/translation";
import { hasTranslatable, sameTranslatable } from "@common/schema/page-element/translatable";

import { groupMembers } from "../translations";

// The languages this page's group holds OTHER than this one, as codes, in the
// site's declared order.
//
// The resolved code (`localeOf`) and not the stored one: a page carrying no
// locale of its own is written in the site's default and says so in
// `<html lang>`, so showing it as blank would name a language nobody chose.
// Same resolution the listing's chips and ../translations' table make.
//
// Ordered by the site's declaration rather than by the order the listing
// happened to return, so the sentence does not reword itself when an unrelated
// page is edited.
export const otherLanguages = (site, page, rows) => {
  const declared = ((site && site.locales) || []).map((entry) => entry && entry.code);
  const codes = groupMembers(rows, page).map((member) => localeOf(site, member));
  const known = declared.filter((code) => codes.includes(code));

  return [...known, ...codes.filter((code) => !declared.includes(code))];
};

// What is shared, said once, above the list that shares it.
//
// Rendered as nothing when the page is in no group or its group has one member,
// which is every page nobody has translated: a sentence about other languages
// on a page that has none is noise on the commonest screen there is.
export const sharedNote = (codes) => {
  if (!codes || codes.length === 0) {
    return null;
  }

  return `These elements, their order and their settings are shared with this page in ${codes.join(
    ", "
  )}. The text inside them is not — that is translated per language.`;
};

// How much of this page is still in the language it was translated FROM.
//
// Only elements with something to translate are counted: a `section` element
// points at a Section row every language already shares — the site's
// translation catalog is what renders that in each of them — and a `code`
// element nobody has filled a value into has no prose either. A page made of
// those reads as done rather than as permanently behind.
export const progressLabel = (page) => {
  const { total, translated } = translationProgress(page);

  return total === 0 ? null : `${translated} of ${total} translated`;
};

// The per-row mark, and the one control that clears it.
//
// Both halves are here rather than only the badge, because the state it shows
// is one an author sometimes has to correct by hand: a translation that is
// legitimately the same string as its source — a product name, a number — is
// done, and nothing the server can see says so.
//
// `value` and `setValue` are the list widget's own, handed to every control it
// renders. Writing through them is the widget's value channel rather than an
// outside write onto the form, which is the difference between a change that
// survives and one the list reverts on its next render (form-schema.md § Bite
// one).
export const TranslationMark = ({ value, setValue }) => {
  if (!value || !value.translationPending || !hasTranslatable(value)) {
    return null;
  }

  return (
    <button
      className="btn btn-link p-0 mr-2"
      title="This still holds the text it was translated from. Click when it no longer does."
      onClick={(e) => {
        e.preventDefault();
        setValue({ ...value, translationPending: false });
      }}
    >
      <Badge color="warning">needs translating</Badge>
    </button>
  );
};

// Which side of the line the field you are looking at is on.
//
// Two components rather than one with a flag, so a screen names the side it is
// on and a reader of that screen does not have to resolve a boolean. Both draw
// nothing when the page is in no group: on a site with one language there is no
// line, and a note about one would be the only thing on screen mentioning
// translation.
//
// They are a sentence and not a control. What decides the split is
// @common/schema/page-element/translatable, and the server applies it whatever
// any screen says — these exist so an author is not surprised by it.
export const SharedAcross = ({ languages }) =>
  !languages || languages.length === 0 ? null : (
    <div className="text-muted small mb-2">
      <i className="fa fa-link mr-1"></i>
      Shared with this page in {languages.join(", ")} — changing this changes it
      in every language.
    </div>
  );

export const ThisLanguageOnly = ({ languages }) =>
  !languages || languages.length === 0 ? null : (
    <div className="text-muted small mb-2">
      <i className="fa fa-language mr-1"></i>
      This language only. The other {languages.length === 1 ? "language" : "languages"} ({languages.join(", ")}) keep their own.
    </div>
  );

// One element, after an edit to it, with the mark cleared if that edit was the
// translation.
//
// The server applies exactly this rule on save (api/code/src/model/page, via
// `clearedPending`) and the server is what the row ends up holding — so this is
// not the decision, it is the screen keeping up with it. Without it the count
// above the list and the badge on the row both stay wrong until something
// remounts the editor: `Section.Mutation` seeds its value state once per mount,
// so a save's answer does not flow back into the form (that is the same
// property `key={pageId}` exists to control, and it is deliberate — see
// ./index).
//
// It clears and never sets, which is the same asymmetry the API has: an author
// editing a translation has finished it, and nothing an author does in this
// editor makes an element newly untranslated.
export const translatedNow = (before, after) =>
  after && before && before.translationPending && !sameTranslatable(before, after)
    ? { ...after, translationPending: false }
    : after;
